import React from "react";
import { Card, CardContent } from "ui/card";
import { Badge } from "ui/badge";
import { Input } from "ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Building2,
  MapPin,
  Users,
  BedDouble,
  // Calendar,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { FormData, RoomAvailability } from "types/instantBookingTypes";
import { InstantAvailabilityVenue } from "services/tourService";

interface AvailableRoomsProps {
  formData: FormData;
  roomAvailability: RoomAvailability[];
  expandedProperties: Set<string>;
  errors: Record<string, string>;
  availabilityData: InstantAvailabilityVenue[];
  getVenueName: (venueId: number | null) => string;
  getDatesInRange: (startDate: string, endDate: string) => Date[];
  handleRoomRequestUpdate: (
    venueId: number | null,
    propertyId: string,
    roomType: string,
    checkInDate: string,
    checkOutDate: string,
    value: number,
  ) => void;
  togglePropertyExpand: (propertyKey: string) => void;
  getTotalRequestedCapacity: () => {
    min: number;
    max: number;
  };
}

export default function AvailableRooms({
  formData,
  roomAvailability,
  expandedProperties,
  errors, 
  availabilityData,
  getVenueName,
  getDatesInRange,
  handleRoomRequestUpdate,
  togglePropertyExpand,
  getTotalRequestedCapacity,
}: AvailableRoomsProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Room Selection - Property-wise Availability
        </h2>
        <p className="text-sm text-gray-600 mb-2">
          View overall room availability for all selected dates. Rooms shown are
          available for the entire stay period.
        </p>
        {formData.venues
          .filter((vs) => vs.fromDate && vs.toDate && vs.venueId)
          .map((schedule) => {
            const venueData = availabilityData.find(
              (v) =>
                v.venueId === schedule.venueId &&
                v.checkInDate.split("T")[0] === schedule.fromDate &&
                v.checkOutDate.split("T")[0] === schedule.toDate,
            );

            const venueProperties = venueData?.properties || [];
            if (venueProperties.length === 0) return null;
            const dateRange = getDatesInRange(
              schedule.fromDate,
              schedule.toDate,
            );
            return (
              <div key={schedule.id} className="mb-4">
                <Card className="border-2 border-blue-200" style={{ marginTop: "0px" }}>
                  <CardContent className="p-4 bg-white rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">
                          {getVenueName(schedule.venueId)}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {schedule.fromDate
                            ? new Date(schedule.fromDate).toLocaleDateString(
                                "en-GB",
                              )
                            : "-"}{" "}
                          to{" "}
                          {schedule.toDate
                            ? new Date(schedule.toDate).toLocaleDateString(
                                "en-GB",
                              )
                            : "-"}{" "}
                          (
                          {schedule.fromDate && schedule.toDate
                            ? Math.max(
                                1,
                                Math.ceil(
                                  (new Date(schedule.toDate).getTime() -
                                    new Date(schedule.fromDate).getTime()) /
                                    (1000 * 60 * 60 * 24),
                                ),
                              )
                            : 0}{" "}
                          nights, {dateRange.length} days)
                        </p>
                      </div>
                    </div>

                    {/* Properties for this venue */}
                    <div className="space-y-4 mt-4">
                      {venueProperties.map((property) => {
                        const propertyRooms = roomAvailability.filter(
                          (ra) =>
                            ra.venue === schedule.venueId &&
                            ra.propertyId === property.propertyId.toString() &&
                            ra.checkInDate === schedule.fromDate &&
                            ra.checkOutDate === schedule.toDate,
                        );
                        const propertyKey = `${property.propertyId}-${property.checkInDate}-${property.checkOutDate}`;
                        const isExpanded = expandedProperties.has(propertyKey);
                        return (
                          <Card
                            key={`${property.propertyId}-${schedule.fromDate}-${schedule.toDate}`}
                            className="border-gray-200 overflow-visible relative"
                          >
                            <CardContent
                              className="overflow-visible relative"
                              style={{ padding: "0px" }}
                            >
                              {/* Property Header */}
                              <div
                                className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b rounded-xl cursor-pointer hover:from-purple-100 hover:to-blue-100 transition-colors"
                                onClick={() =>
                                  togglePropertyExpand(propertyKey)
                                }
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                                      <Building2 className="h-5 w-5 text-purple-600" />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                        {property.propertyName}
                                      </h4>
                                      <p className="text-xs text-gray-600 mt-1">
                                        Available for all {dateRange.length}{" "}
                                        days • Click to expand/collapse
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="text-right">
                                      <div className="text-sm text-gray-600">
                                        Total Requested
                                      </div>
                                      <div className="text-lg font-bold text-purple-600">
                                        {propertyRooms.reduce(
                                          (sum, r) => sum + r.requested,
                                          0,
                                        )}{" "}
                                        rooms
                                      </div>
                                    </div>
                                    {isExpanded ? (
                                      <ChevronUp className="h-5 w-5 text-gray-400" />
                                    ) : (
                                      <ChevronDown className="h-5 w-5 text-gray-400" />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Room Types Table */}
                              {isExpanded && (
                                <div
                                  className="bg-white rounded-xl"
                                  style={{
                                    padding: "16px",
                                    borderRadius: "0px 0px 16px 16px",
                                  }}
                                >
                                  <div className="overflow-x-auto overflow-y-visible relative">
                                    <Table className="overflow-visible">
                                      <TableHeader>
                                        <TableRow className="bg-gray-50">
                                          <TableHead className="text-xs">
                                            Room
                                          </TableHead>
                                          <TableHead className="text-center text-xs">
                                            Min Cap.
                                          </TableHead>
                                          <TableHead className="text-center text-xs">
                                            Max Cap.
                                          </TableHead>
                                          <TableHead className="text-center text-xs">
                                            Overall Available
                                          </TableHead>
                                          <TableHead className="text-center text-xs">
                                            Request
                                          </TableHead>
                                          <TableHead className="text-right text-xs">
                                            Total Capacity
                                          </TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody className="overflow-visible">
                                        {propertyRooms.map(
                                          (room, roomIndex) => (
                                            <React.Fragment
                                              key={`${room.propertyId}-${room.roomType}`}
                                            >
                                              {/* Main Room Row */}
                                              <TableRow
                                                className="overflow-visible relative"
                                                style={{
                                                  border: "none",
                                                }}
                                              >
                                                <TableCell className="text-xs">
                                                  <div className="flex items-center gap-2">
                                                    <BedDouble className="h-3.5 w-3.5 text-gray-400" />
                                                    <span className="font-medium">
                                                      {room.roomTypeName}
                                                    </span>
                                                  </div>
                                                </TableCell>

                                                <TableCell className="text-center text-xs">
                                                  <Badge
                                                    variant="outline"
                                                    className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                                                  >
                                                    {room.minCapacity}
                                                  </Badge>
                                                </TableCell>

                                                <TableCell className="text-center text-xs">
                                                  <Badge
                                                    variant="outline"
                                                    className="text-xs bg-purple-50 text-purple-700 border-purple-200"
                                                  >
                                                    {room.maxCapacity}
                                                  </Badge>
                                                </TableCell>

                                                <TableCell className="text-center text-xs overflow-visible relative">
                                                  <div className="relative group inline-block overflow-visible">
                                                    <Badge className="bg-green-100 text-green-800 border-green-200 text-xs font-bold cursor-pointer hover:bg-green-200 transition-colors">
                                                      {room.overallAvailable}
                                                    </Badge>
                                                    {/* <div
                                                      className={`
                                                        absolute z-[999999] hidden group-hover:block left-1/2 -translate-x-1/2
                                                        ${
                                                          roomIndex >=
                                                          propertyRooms.length -
                                                            2
                                                            ? "bottom-full mb-2"
                                                            : "top-full mt-2"
                                                        }
                                                        min-w-[450px] rounded-xl border border-gray-200 bg-white shadow-[0_12px_40px_rgba(0,0,0,0.18)] px-4 py-3`}
                                                    >
                                                      <div className="flex items-center gap-2 mb-2">
                                                        <Calendar className="h-4 w-4 text-gray-500" />
                                                        <span className="text-xs font-semibold text-gray-700">
                                                          Daily Availability
                                                          Breakdown
                                                        </span>
                                                      </div>
                                                      <div className="grid grid-cols-4 gap-2">
                                                        {room.dailyAvailability.map(
                                                          (day, idx) => (
                                                            <div
                                                              key={idx}
                                                              className="flex items-center justify-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-2 min-w-[100px]"
                                                            >
                                                              <span className="text-[11px] font-medium text-gray-600">
                                                                {new Date(
                                                                  day.date,
                                                                ).toLocaleDateString(
                                                                  "en-GB",
                                                                  {
                                                                    day: "2-digit",
                                                                    month:
                                                                      "short",
                                                                  },
                                                                )}
                                                              </span>
                                                              <span className="text-gray-300">
                                                                -
                                                              </span>
                                                              <Badge
                                                                variant="outline"
                                                                className={`text-[10px] h-5 px-2 ${
                                                                  day.available ===
                                                                  room.overallAvailable
                                                                    ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                                                    : "bg-green-50 text-green-700 border-green-200"
                                                                }`}
                                                              >
                                                                {day.available}
                                                              </Badge>
                                                            </div>
                                                          ),
                                                        )}
                                                      </div>
                                                    </div> */}
                                                  </div>
                                                </TableCell>
                                                <TableCell className="text-xs">
                                                  <Input
                                                    type="number"
                                                    min={0}
                                                    max={room.overallAvailable}
                                                    step={1}
                                                    value={room.requested}
                                                    onChange={(e) => {
                                                      let value =
                                                        parseInt(
                                                          e.target.value,
                                                        ) || 0;

                                                      if (value < 0) value = 0;

                                                      if (
                                                        value >
                                                        room.overallAvailable
                                                      ) {
                                                        value =
                                                          room.overallAvailable;
                                                      }

                                                      handleRoomRequestUpdate(
                                                        schedule.venueId,
                                                        room.propertyId,
                                                        room.roomType,
                                                        room.checkInDate,
                                                        room.checkOutDate,
                                                        value,
                                                      );
                                                    }}
                                                    className="w-20 text-center mx-auto h-8 text-xs"
                                                  />
                                                </TableCell>

                                                <TableCell className="text-right font-medium text-xs text-purple-600">
                                                  {room.requested *
                                                    room.maxCapacity}{" "}
                                                  pax
                                                </TableCell>
                                              </TableRow>
                                            </React.Fragment>
                                          ),
                                        )}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}

        {formData.venues.filter((vs) => vs.fromDate && vs.toDate && vs.venueId)
          .length === 0 && (
          <Card className="border-dashed border-2">
            <CardContent className="p-8 text-center">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 text-sm">
                No venue schedules configured yet
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Please configure venue schedules in the previous tab
              </p>
            </CardContent>
          </Card>
        )}

        {formData.venues.filter((vs) => vs.fromDate && vs.toDate && vs.venueId)
          .length > 0 && (
          <div>
            <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-700 mb-2">
                      Total Tour Capacity
                    </div>
                    <div className="text-xs text-gray-600">
                      Based on minimum and maximum room capacity
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2">
                      <Users className="h-4 w-4 mr-2" />
                      Min: {getTotalRequestedCapacity().min} pax - Max:{" "}
                      {getTotalRequestedCapacity().max} pax
                    </Badge>
                  </div>
                </div>
                {errors.rooms && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-red-500">
                      {errors.rooms}
                    </p>
                  </div>
                )}
                {errors.capacity && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-red-500">
                      {errors.capacity}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
